import React, { useContext, useState, useMemo, useCallback, useRef } from 'react';
import moment from 'moment';
import { useActor } from '@xstate/react';
import classNames from 'classnames';
import SnippetOverflowMenu from '../components/SnippetOverflowMenu';
import DescriptionIcon from '@mui/icons-material/Description';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { GlobalStateContext } from '../../state/app';
import logger from '../../logger';
import track from '../../utils/trackers';
import { dryChromeClass } from '../DryChromeClass';
import EmptyEntityInfoPanel from '../panels/EmptyEntityInfoPanel';
import SearchAndSortControls from '../components/SearchAndSortControls';
import Fuse from 'fuse.js';
import NoSearchResults from '../components/NoSearchResults';
import interactionLedger from '../../utils/interactionLedger';
import { sortNotesByDateModified } from '../../utils/helpers';
const formatModifiedDate = (dateString) => {
  if (!dateString) return '';
  const date = moment(dateString);
  const now = moment();

  if (date.isSame(now, 'day')) {
    return `Today • ${date.format('h:mm A')}`;
  } else if (date.isSame(now.clone().subtract(1, 'days'), 'day')) {
    return `Yesterday • ${date.format('h:mm A')}`;
  } else {
    return date.format('MMM D • h:mm A');
  }
};

const OpenNotesPanel = () => {
  const globalServices = useContext(GlobalStateContext);
  const [appState, sendAppState] = useActor(globalServices.appState);
  const {
    notes,
    selectedNoteId,
    selectedAreaOfFocus,
    areaOfFocusOpenNoteIds,
    areasOfFocusCollectionsIterable,
  } = appState.context;
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef(null);
  const [sortBy, setSortBy] = useState('modified');
  const [sortDirection, setSortDirection] = useState('desc');
  const [aofPickerOpen, setAofPickerOpen] = useState(false);

  const handleSelectAof = (collectionId) => {
    sendAppState({ type: 'selectAreasOfFocusCollection', data: { collectionId } });
    sendAppState({ type: 'saveSelectedAOFUserSettings', data: { collectionId } });
    setAofPickerOpen(false);
  };

  // Create Fuse instance for fuzzy searching
  const fuse = useMemo(() => {
    const openNotes = (areaOfFocusOpenNoteIds || [])
      .map((id) => notes[id])
      .filter((note) => note); // Filter out undefined notes

    return new Fuse(openNotes, {
      keys: [
        { name: 'note_title', weight: 2 }, // Title matches are more important
        { name: 'note_snippet', weight: 1 },
        { name: 'note_body', weight: 0.5 },
      ],
      includeScore: true, // Include score for better sorting
      threshold: 0.4, // Lower threshold means more strict matching
      minMatchCharLength: 2, // Minimum length of pattern that can be matched
    });
  }, [notes, areaOfFocusOpenNoteIds]);

  const clearSearch = () => {
    setSearchTerm('');
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
  };

  const handleTabClick = (noteId) => {
    if (!noteId) {
      logger.error(
        { noteId },
        'OpenNotesPanel: handleTabClick called with invalid noteID'
      );
      return;
    }

    track.event('notes', 'click_note_document_tab', {
      options: {
        note_id: noteId,
      },
    });
    track.event('notes', 'view_note_document', {
      options: {
        note_id: noteId,
      },
    });

    sendAppState({
      type: 'selectNote',
      data: {
        noteId,
      },
    });

    clearSearch();
    sendAppState({ type: 'toggleOpenNotesPanelView' });
    sendAppState({ type: 'SelectTab', tabName: 'editorWrapper' });
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  // Stable sort order — only recomputes when the open note list or sort criteria
  // change. Intentionally excludes `notes` to prevent the list from reordering
  // while the user is typing (saves update date_modified, which would otherwise
  // bounce the active note up/down on every debounced save).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableOrderedIds = useMemo(() => {
    let usageCounts = {};
    let lastUsed = {};
    try {
      const noteSelections = interactionLedger.getByType('noteSelection') || [];
      noteSelections.forEach((log) => {
        const noteId = log.data?.currentNoteId || log.data?.noteId || log.data?.note_id;
        if (noteId) {
          usageCounts[noteId] = (usageCounts[noteId] || 0) + 1;
          const timestamp = log.time || (log.timestamp ? new Date(log.timestamp).getTime() : Date.now());
          if (!lastUsed[noteId] || timestamp > lastUsed[noteId]) {
            lastUsed[noteId] = timestamp;
          }
        }
      });
    } catch (error) {
      logger.warn({ error }, 'Failed to get usage stats from interaction ledger');
    }

    const noteItems = (areaOfFocusOpenNoteIds || []).map((id) => notes[id]).filter(Boolean);
    const sorted = sortNotesByDateModified({
      noteList: noteItems,
      sortBy,
      sortDirection,
      usageStats: { usageCounts, lastUsed },
    });
    return sorted.map((n) => n?.note_id).filter(Boolean);
  }, [areaOfFocusOpenNoteIds, sortBy, sortDirection]); // `notes` intentionally excluded

  // Map stable IDs to fresh note objects for display; search overrides with Fuse order.
  const sortedNotes = useMemo(() => {
    if (searchTerm) return fuse.search(searchTerm);
    return stableOrderedIds
      .map((id) => ({ item: notes[id], score: 0 }))
      .filter((r) => r.item);
  }, [searchTerm, stableOrderedIds, fuse, notes]);

  // When searching, the top result is highlighted and can be opened with Enter
  const highlightedNoteId =
    searchTerm && sortedNotes.length > 0 ? sortedNotes[0]?.item?.note_id : null;

  // G: useCallback so a new function reference is only produced when the
  // highlighted note or the tab-click handler actually changes.
  const handleSearchKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && highlightedNoteId) {
        handleTabClick(highlightedNoteId);
      }
    },
    [highlightedNoteId, handleTabClick]
  );

  // H: declarative .map() instead of an imperative for-of / push loop
  const openNoteEditors = sortedNotes
    .map(({ item: element, score = 0 }) => {
      if (!element) {
        logger.error(
          { noteId: element?.note_id },
          'renderNoteEditor Failed to load noteId'
        );
        return null;
      }

      const {
        note_id: noteId,
        note_title: noteTitle,
        date_modified: dateModified,
        note_type: noteType,
      } = element;
      const isSelected = noteId && selectedNoteId === noteId;
      const isHighlighted = noteId && highlightedNoteId === noteId;
      const mobileTabClass = classNames('tab-mobile', {
        selected: isSelected,
        highlighted: isHighlighted,
        'high-relevance': searchTerm && score < 0.2,
        'medium-relevance': searchTerm && score >= 0.2 && score < 0.4,
      });

      return (
        <div className={mobileTabClass} key={noteId} data-noteid={noteId}>
          {/* Clickable area: icon + text only — does NOT include the overflow zone */}
          <div
            className="tab-clickable-area"
            onPointerDown={(e) => {
              if (e.pointerType === 'touch' || e.pointerType === 'pen') {
                e.preventDefault();
              }
              handleTabClick(noteId);
            }}
            onClick={() => {
              // Fallback for browsers/devices that don't support pointer events
              handleTabClick(noteId);
            }}
            role="button"
            aria-label={`Select note: ${noteTitle || 'untitled'}`}
          >
            <div className="tab-icon">
              {noteType === 'llm' || noteType === 'ai' ? (
                <SmartToyIcon fontSize="small" />
              ) : (
                <DescriptionIcon fontSize="small" />
              )}
            </div>
            <div className="tab-text-container">
              <span className="tab-text" title={noteTitle || 'untitled'}>
                {noteTitle || 'untitled'}
                {searchTerm && score < 0.4 && (
                  <span className="relevance-score">
                    {score < 0.2 ? '(Highly Relevant)' : '(Relevant)'}
                  </span>
                )}
              </span>
              <span className="tab-subtitle">
                {isSelected ? 'Active now' : formatModifiedDate(dateModified)}
              </span>
            </div>
          </div>
          {/* Action zone: dead space + overflow menu — never triggers note selection */}
          <div className="tab-action-zone">
            <SnippetOverflowMenu
              snippetNoteId={noteId}
              section="notes"
              isNoteOpen={true}
            />
          </div>
        </div>
      );
    })
    .filter(Boolean);

  const aofName = selectedAreaOfFocus?.title || 'All Notes';

  return (
    <div className="open-notes-panel">
      <div className="open-notes-panel__aof">
        <button
          className="open-notes-panel__aof-btn"
          onClick={() => setAofPickerOpen((v) => !v)}
          type="button"
          aria-expanded={aofPickerOpen}
        >
          <i className="material-icons">dashboard</i>
          <span className="open-notes-panel__aof-name">{aofName}</span>
          <i className="material-icons open-notes-panel__aof-caret">
            {aofPickerOpen ? 'expand_less' : 'expand_more'}
          </i>
        </button>
        {aofPickerOpen && (
          <ul className="open-notes-panel__aof-list">
            {(areasOfFocusCollectionsIterable || []).map((aof) => {
              const id = aof.area_of_focus_collection_id;
              const name = aof.title;
              const isActive = id === selectedAreaOfFocus?.area_of_focus_collection_id;
              return (
                <li
                  key={id}
                  className={`open-notes-panel__aof-option${isActive ? ' active' : ''}`}
                  onClick={() => handleSelectAof(id)}
                  role="option"
                  aria-selected={isActive}
                >
                  {isActive && <i className="material-icons">check</i>}
                  <span>{name}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div className="open-notes-panel__header">
        <SearchAndSortControls
          ref={searchInputRef}
          onSearch={handleSearch}
          onKeyDown={handleSearchKeyDown}
          onSortChange={setSortBy}
          onSortDirectionChange={setSortDirection}
          placeholder="Search open notes..."
          sortBy={sortBy}
          sortDirection={sortDirection}
        />
      </div>
      <div className="note-tabs-list">
        {openNoteEditors.length > 0 ? (
          openNoteEditors
        ) : searchTerm ? (
          <NoSearchResults />
        ) : (
          <EmptyEntityInfoPanel />
        )}
      </div>
    </div>
  );
};

export default OpenNotesPanel;
